<template>
    <div class="custom-input-calendar flex gap-2 align-items-center custom-calendar relative">
        <CustomLabel :name="name" :label="label" :required="required" :disabled="disabled" />
        <Calendar
            :class="computedClasses"
            :id="name"
            v-model="calendarValue"
            :showTime="showTime"
            :timeOnly="timeOnly"
            :dateFormat="dateFormat"
            :selectionMode="selectionMode"
            :manualInput="true"
            :showOnFocus="true"
            :placeholder="computedPlaceholder"
            :view="view"
            :showButtonBar="showButtonBar"
            @show="applyYearRangeHighlight(calendarValue)"
            :disabled="disabled"
            :max-date="maxDate"
            :rules="rules"
        />
        <CustomValidationMessage :errorMessage="errorMessage" />
        <CustomValidationMessage :errorMessage="customErrorMessage" />
    </div>
</template>

<script>
    import Calendar from "primevue/calendar";
    import {useField} from "vee-validate";
    import CustomLabel from "./inner/CustomLabel";
    import CustomValidationMessage from "./inner/CustomValidationMessage";
    import { DateUtils } from "@/utils/DateUtils";
    import {watch} from "vue";

    export default {
        name: "CustomCalendar",

        props: {
            name: {
                type: String,
            },
            label: {
                type: String,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            timeOnly: {
                type: Boolean,
                default: false,
            },
            selectionMode: {
                type: String,
                default: "single",
            },
            noPlaceholder: {
                type: Boolean,
                default: false,
            },
            required: {
                type: Boolean,
                default: false,
            },
            modelValue: null, // Expected to be string or array of strings
            rules: undefined,
            showTime: {
                type: Boolean,
                default: false,
            },
            customErrorMessage: {
                type: String,
                default: "",
            },
            placeholder: {
                type: String,
                default: "DD-MM-YYYY",
            },
            view: {
                type: String,
                default: "date",
            },
            dateFormat: {
                type: String,
                default: "dd-mm-yy",
            },
            showButtonBar: {
                type: Boolean,
                default: false,
            },
            maxDate: {
                default: new Date(),
            },
        },
        emits: ["update:modelValue"],

        computed: {
            computedClasses() {
                return this.errorMessage ? "p-invalid" : "";
            },

            // Convert string values to Date objects for the Calendar component
            calendarValue: {
                get() {
                    return this.stringToDateValue(this.modelValue);
                },
                set(dateValue) {
                    const stringValue = this.dateToStringValue(dateValue);
                    this.handleChange(stringValue);
                    this.$emit("update:modelValue", stringValue);
                },
            },

            computedPlaceholder() {
                if (this.noPlaceholder) return "";
                return this.placeholder || this.$t("message.inputs.calendarPlaceholder");
            },
        },

        methods: {
            // Convert string(s) to Date object(s) for Calendar component
            stringToDateValue(stringValue) {
                if (!stringValue) return null;

                if (this.selectionMode === 'single') {
                    return this.parseStringToDate(stringValue);
                }
                else if (this.selectionMode === 'range' && Array.isArray(stringValue)) {
                    return stringValue.length === 2 ?
                        stringValue.map(str => this.parseStringToDate(str)).filter(Boolean) :
                        null;
                }
                else if (this.selectionMode === 'multiple' && Array.isArray(stringValue)) {
                    return stringValue.map(str => this.parseStringToDate(str)).filter(Boolean);
                }

                return null;
            },

            // Convert Date object(s) to string(s) for external use
            dateToStringValue(dateValue) {
                if (!dateValue) return null;

                if (this.selectionMode === 'single') {
                    return this.formatDateToString(dateValue);
                }
                else if (this.selectionMode === 'range' && Array.isArray(dateValue)) {
                    return dateValue.length === 2 ?
                        dateValue.map(date => this.formatDateToString(date)) :
                        null;
                }
                else if (this.selectionMode === 'multiple' && Array.isArray(dateValue)) {
                    return dateValue.map(date => this.formatDateToString(date));
                }

                return null;
            },

            // Parse string to Date object
            parseStringToDate(dateString) {
                //assume it is only year
                if (dateString && typeof dateString === 'number') {
                    return new Date(dateString, 0, 1); // January 1st,
                }

                if (!dateString || typeof dateString !== 'string') return null;

                try {
                    // Handle different input formats
                    if (dateString.includes('-')) {
                        // Handle DD-MM-YYYY or YYYY-MM-DD formats
                        const parts = dateString.split('-');
                        if (parts.length === 3) {
                            // Check if it's DD-MM-YYYY format
                            if (parts[0].length <= 2) {
                                const [day, month, year] = parts;
                                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            } else {
                                // Assume YYYY-MM-DD format
                                return new Date(dateString);
                            }
                        }
                    }

                    // Fallback to native Date parsing
                    const date = new Date(dateString);
                    return isNaN(date.getTime()) ? null : date;
                } catch (error) {
                    console.warn('Failed to parse date string:', dateString, error);
                    return null;
                }
            },

            // Format Date object to string based on dateFormat prop
            formatDateToString(date) {
                if (!date || !(date instanceof Date)) return null;

                const dateObj = new Date(date);
                if (isNaN(dateObj.getTime())) return null;

                let formattedDate = this.dateFormat;

                const day = dateObj.getDate().toString().padStart(2, '0');
                const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                const year = dateObj.getFullYear();
                const hours = dateObj.getHours().toString().padStart(2, '0');
                const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                const seconds = dateObj.getSeconds().toString().padStart(2, '0');

                formattedDate = formattedDate.replace('dd', day);
                formattedDate = formattedDate.replace('mm', month);
                formattedDate = formattedDate.replace('yy', year);

                if (this.showTime) {
                    formattedDate += ` ${hours}:${minutes}:${seconds}`;
                }

                return formattedDate;
            },

            applyYearRangeHighlight(newRange) {
                if (newRange != null && this.view === "year" && this.selectionMode === "range") {
                    if (Array.isArray(newRange) && newRange.length === 2) {
                        const [startYear, endYear] = newRange;
                        const newStartYear = Number(DateUtils.getYearOnly(startYear));
                        const newEndYear = Number(DateUtils.getYearOnly(endYear));
                        const yearTiles = document.querySelectorAll('.p-yearpicker-year');

                        yearTiles.forEach((tile) => {
                            tile.classList.remove('p-highlight', 'p-highlight-start', 'p-highlight-end');
                        });

                        yearTiles.forEach((tile) => {
                            const year = parseInt(tile.textContent.trim());
                            if (year >= newStartYear && year <= newEndYear) {
                                tile.classList.add('p-highlight');
                            }
                        });
                    }
                }
            },
        },

        watch: {
            calendarValue: {
                handler(value) {
                    this.applyYearRangeHighlight(value);
                },
                deep: true,
            },
        },

        setup(props) {
            const {
                value: inputValue,
                errorMessage,
                handleChange,
                meta,
                setValue
            } = useField(props.name, props.rules, {
                initialValue: props.modelValue,
            });

            watch(
                () => props.modelValue,
                (newValue) => {
                    setValue(newValue);
                }
            );

            return {
                handleChange,
                errorMessage,
                inputValue,
                meta,
            };
        },

        components: {Calendar, CustomLabel, CustomValidationMessage},
    };
</script>

<style scoped lang="scss">
.custom-calendar {
    .p-calendar {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }
}
</style>
